import { Link } from 'react-router-dom';
import { ArrowLeft, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const GROUPS: { title: string; fields: { name: string; desc: string }[] }[] = [
  {
    title: 'Identity & governance',
    fields: [
      { name: 'atom_id', desc: 'Stable unique id for the atom.' },
      { name: 'version', desc: 'Schema version — news-atom/1.0.' },
      { name: 'atom_status', desc: 'Lifecycle: active · superseded · retracted.' },
      { name: 'supersedes_atom_id', desc: 'The atom this one replaces (audit trail).' },
    ],
  },
  {
    title: 'Epistemic signals',
    fields: [
      { name: 'knowledge_frame', desc: 'Allan Bell news-text role (see below).' },
      { name: 'statement', desc: 'The atom itself — a self-contained claim/sentence.' },
      { name: 'semantic_frame', desc: 'actor · action · object · location.' },
      { name: 'primary_expression', desc: 'Canonical phrasing (verbatim for quotes).' },
    ],
  },
  {
    title: 'Provenance',
    fields: [
      { name: 'media_anchor[]', desc: 'Source(s) + in-clip timecode. Multiple = corroboration.' },
      { name: 'origin', desc: 'Producer (heuristic / gemini-via-vertex), when, tool version.' },
      { name: 'review_process', desc: 'Extraction method + confidence + candidate→approved/rejected.' },
      { name: 'license', desc: 'Granular terms of use / syndication.' },
    ],
  },
  {
    title: 'Relational context',
    fields: [
      { name: 'event_frame', desc: 'Link to the EVENT this atom belongs to.' },
      { name: 'topic_id', desc: 'The topic the atom was extracted for.' },
      { name: 'language', desc: 'BCP-47 language of the statement.' },
    ],
  },
];

const FRAMES = [
  'action',
  'reaction',
  'consequence',
  'context',
  'evaluation',
  'expectation',
  'previous_episode',
  'history',
  'narrative',
];

export default function Schema() {
  return (
    <div className="container mx-auto max-w-3xl px-6 py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Link>
      </Button>

      <h1 className="flex items-center gap-2 text-2xl font-semibold">
        <Boxes className="h-6 w-6 text-violet-600" />
        The atom schema we use
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Atoms implement the <strong>Reuters Institute “News Atom” v1.0</strong> schema (Sannuta Raghu, 2025) — the smallest
        verifiable, reusable unit of news. Its 15 fields, grouped as in the blueprint:
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-lg border bg-card p-4 shadow-sm">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</p>
            <ul className="space-y-2">
              {g.fields.map((f) => (
                <li key={f.name} className="text-sm">
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px]">{f.name}</code>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{f.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h2 className="mt-8 text-lg font-semibold">knowledge_frame values</h2>
      <p className="mt-1 text-sm text-muted-foreground">Allan Bell’s nine news-text roles:</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {FRAMES.map((f) => (
          <Badge key={f} variant="secondary" className="capitalize">
            {f.replace('_', ' ')}
          </Badge>
        ))}
      </div>

      <h2 className="mt-8 text-lg font-semibold">Lineage</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Following the blueprint’s flow, atoms roll up from the smallest unit to the archive:
      </p>
      <div className="mt-2 rounded-lg border bg-card p-3 font-mono text-sm">SENTENCE → EVENT → EVENT-BANK → ARCHIVE</div>

      <p className="mt-6 text-xs text-muted-foreground">
        App extensions (not part of News Atom v1.0): <code>atom_type</code> (UI classification), <code>entities</code>,{' '}
        <code>tags</code>. Approved atoms are published to Iconik for the Story Engine.
      </p>
    </div>
  );
}
